import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import DialogContentText from "@mui/material/DialogContentText";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import ResponsiveDialog from "@nualang/nualang-ui-components/dist/Dialogs/ResponsiveDialog";
import { getGroupMembers, createGroupMember } from "@nualang/nualang-api-and-queries/APIs/GroupMembers";
import { getGroup } from "@nualang/nualang-api-and-queries/APIs/Groups";
import { groups as groupsQuery } from "@nualang/nualang-api-and-queries/Queries";
import Loading from "../../../components/Misc/Loading";
import { useApp } from "../../../hooks";

import logger from "../../../logger";

function JoinGroup(props) {
  const { t } = useTranslation();
  const {
    authData: { username, attributes },
    navigate,
  } = props;

  const params = useParams();
  const { groupId } = params;

  const { startLoading, stopLoading, openSnackbar } = useApp();

  const [dialogState, setDialogState] = useState({
    isOpen: false,
    title: "",
    description: "",
  });
  const [loading, setLoading] = useState(true);

  const handleOpenDialog = (title, description) => {
    setDialogState({
      isOpen: true,
      title,
      description,
    });
  };

  const handleCloseDialog = () => {
    setDialogState((prevState) => ({
      ...prevState,
      isOpen: false,
    }));
    navigate("/explore");
  };

  const joinGroupMutation = groupsQuery.useJoinGroup(createGroupMember, {
    onMutate: () => {
      startLoading()
    },
    onSettled: () => {
      stopLoading()
    },
    onError: () => {
      openSnackbar("problem_creating", "error");
    }
  });

  const handleJoinGroup = useCallback(async () => {
    try {
      const groupResponse = await getGroup(groupId);
      const groupCurrentSize = await getGroupMembers(groupId);
      const { groupName, allowedDomains } = groupResponse.Item;
      let domainMatch = false;
      for (let index = 0; index < allowedDomains.length; index += 1) {
        const domain = allowedDomains[index];
        if ((attributes.email || "").endsWith(domain)) {
          domainMatch = true;
        }
      }
      if (
        domainMatch &&
        groupCurrentSize.Count >= groupResponse.Item.memberLimit
      ) {
        setLoading(false);
        handleOpenDialog(
          t("unable_to_join_subscription"),
          t("unable_to_join_subscription_limit_reached_desc", { groupName }),
        );
      } else if (domainMatch) {
        const response = await joinGroupMutation.mutateAsync({groupId: groupId, memberId: username});
        if (response === false) {
          throw new Error();
        }
        setLoading(false);
        handleOpenDialog(
          t("joined_subscription"),
          t("joined_subscription_desc", { groupName }),
        );
      } else {
        setLoading(false);
        handleOpenDialog(
          t("unable_to_join_subscription"),
          t("unable_to_join_subscription_invalid_email_desc", {
            allowedDomains: allowedDomains.toString(),
          }),
        );
      }
    } catch (error) {
      logger.error(error);
      setLoading(false);
      handleOpenDialog(
        t("unable_to_join_subscription"),
        t("unable_to_join_subscription_desc"),
      );
    }
  }, [attributes.email, groupId, t, username, joinGroupMutation]);

  useEffect(() => {
    handleJoinGroup();
  }, [handleJoinGroup]);

  return (
    <div>
      {
        <>
          {loading && <Loading />}
          <ResponsiveDialog
            open={dialogState.isOpen}
            handleClose={handleCloseDialog}
            dialogTitle={dialogState.title}
          >
            <DialogContentText>{dialogState.description}</DialogContentText>
          </ResponsiveDialog>
        </>
      }
    </div>
  );
}

JoinGroup.propTypes = {
  authData: PropTypes.object,
  navigate: PropTypes.func,
};

export default JoinGroup;
